import React, { useEffect, useLayoutEffect, useRef, useState, useCallback, useMemo } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../contexts/AuthContext';
import { useFavoritesContext } from '../contexts/FavoritesCountContext';
import { useProfile } from '../contexts/ProfileContext';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { StartupProfile } from '../types/firestore';
import FeaturedStartup from './FeaturedStartup';
import DashboardWelcomeCallout from './DashboardWelcomeCallout';
import StartupReviewCallout from './StartupReviewCallout';
import StartupProfileComponent from './profile/StartupProfile';
import { useTracking } from '../utils/useTracking';
import { useViewedStartups } from '../utils/useViewedStartups';
import { countryRegions } from '../constants/countries';
import {
  CATALOG_CATEGORY_LABELS,
  getSinceYouFollowBadgeLabel,
  isSinceYouAddedFavoritesTitle,
  type CatalogCategory,
} from '../constants/catalog-categories';
import StartupRow from './StartupRow';
import DismissalConfirmationToast from './DismissalConfirmationToast';
import { inferCatalogBrowsingCategory } from '../utils/inferCatalogBrowsingCategory';
import { inferCategoryFromCatalogRows } from '../utils/inferCategoryFromCatalogRows';
import {
  buildBasedOnFavoritesStartups,
  buildInvestorCatalogFeed,
  normalizePreferredCountries,
  startupMatchesPreferredCountries,
} from '../utils/buildPostFavoritesCatalogFeed';
import { resolveStartupId } from '../utils/startupId';
import { PRAIRI_SURFACES } from '../constants/prairiSurfaces';
import { startupMatchesNameSlug, startupNameSlug } from '../utils/startupNameSlug';
import { withoutStartupProfileParams } from '../utils/startupProfileHistory';
import {
  forgetOpenStartupProfile,
  readOpenStartupProfile,
  rememberOpenStartupProfile,
} from '../utils/openStartupProfile';
import {
  dashboardWelcomeIsDue,
  readDashboardWelcomeVisit,
  recordDashboardWelcomeVisit,
} from '../utils/dashboardWelcome';
import {
  readStartupReviewVisit,
  recordStartupReviewVisit,
  shouldShowStartupReviewCallout,
  STARTUP_REVIEW_CALLOUT_DELAY_MS,
  startupReviewCalloutIsDue,
} from '../utils/startupReviewCallout';

function jumpToPageTop() {
  window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
}

function releaseCompositorLayers() {
  document.body.style.transform = 'translateZ(0)';
  requestAnimationFrame(() => {
    document.body.style.transform = '';
  });
}

/**
 * The row the investor just clicked. A URL open must not replace it with a
 * guessed label (often Women-led) when the profile address changes.
 */
let pendingCatalogClick: {
  startupId: string;
  slug?: string;
  category?: CatalogCategory;
} | null = null;

function categoryFromCatalogClick(
  startupId: string | undefined,
  fallback: CatalogCategory | undefined
): CatalogCategory | undefined {
  if (startupId && pendingCatalogClick?.startupId === startupId && pendingCatalogClick.category) {
    return pendingCatalogClick.category;
  }
  return fallback;
}

/** The catalog row saved when this startup was opened. Survives a refresh in this tab. */
function storedBrowseCategory(
  categories: Record<string, string>,
  ...ids: Array<string | undefined | null>
): CatalogCategory | undefined {
  for (const id of ids) {
    const category = id ? categories[id] : undefined;
    if (category) return category as CatalogCategory;
  }
  return undefined;
}

const InvestorCatalogDashboard: React.FC = () => {
  const { user } = useAuth();
  const { investorProfile } = useProfile();
  const { favorites: ctxFavorites, favoritesReady, invalidateFavorites } = useFavoritesContext();
  // Refs so the fetch effect can read current context values without re-running on cache changes
  const ctxFavoritesRef = useRef(ctxFavorites);
  ctxFavoritesRef.current = ctxFavorites;
  const favoritesReadyRef = useRef(favoritesReady);
  favoritesReadyRef.current = favoritesReady;
  const { trackInteraction } = useTracking();
  const { viewedIds, markViewed, refreshViewed, viewedIdsOrdered, viewedCategories } =
    useViewedStartups();

  const navigate = useNavigate();
  const location = useLocation();
  const { startupSlug } = useParams<{ startupSlug?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [profileUpdatedToastMessage, setProfileUpdatedToastMessage] = useState<string | null>(null);
  const [showDashboardWelcome, setShowDashboardWelcome] = useState(false);
  const [showStartupReviewCallout, setShowStartupReviewCallout] = useState(false);
  const startupReviewBlockedRef = useRef(false);
  const catalogHome = !startupSlug && !searchParams.get('startupId');

  useEffect(() => {
    if (!catalogHome || user?.userType !== 'investor' || !user.id) return;
    const lastVisit = readDashboardWelcomeVisit(user.id);
    if (dashboardWelcomeIsDue(lastVisit)) setShowDashboardWelcome(true);
    // Write after this effect settles so React Strict Mode’s remount still sees the first visit.
    const userId = user.id;
    const timer = window.setTimeout(() => recordDashboardWelcomeVisit(userId), 0);
    return () => window.clearTimeout(timer);
  }, [catalogHome, user?.id, user?.userType]);

  useEffect(() => {
    const toastMsg = (location.state as any)?.toastMessage;
    if (toastMsg) {
      setProfileUpdatedToastMessage(toastMsg);
      window.history.replaceState({}, document.title);
      const timer = setTimeout(() => {
        setProfileUpdatedToastMessage(null);
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [location.state]);
  const [allStartups, setAllStartups] = useState<StartupProfile[]>([]);
  const [matchedStartups, setMatchedStartups] = useState<StartupProfile[]>([]);
  const [favoriteStartups, setFavoriteStartups] = useState<StartupProfile[]>([]);
  const [womenLedStartups, setWomenLedStartups] = useState<StartupProfile[]>([]);
  const [pocOwnedStartups, setPocOwnedStartups] = useState<StartupProfile[]>([]);
  const [lgbtqOwnedStartups, setLgbtqOwnedStartups] = useState<StartupProfile[]>([]);
  const [recentlyAddedStartups, setRecentlyAddedStartups] = useState<StartupProfile[]>([]);
  const [sdgAlignedStartups, setSdgAlignedStartups] = useState<StartupProfile[]>([]);
  const [mostPopularStartups, setMostPopularStartups] = useState<StartupProfile[]>([]);
  const [selectedStartup, setSelectedStartup] = useState<StartupProfile | null>(null);
  const [selectedStartupCategory, setSelectedStartupCategory] = useState<
    CatalogCategory | undefined
  >(undefined);
  const [loading, setLoading] = useState(true);
  const [matchesReady, setMatchesReady] = useState(false);
  const [catalogReady, setCatalogReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedFavoriteForSimilar, setSelectedFavoriteForSimilar] =
    useState<StartupProfile | null>(null);
  const [dismissedStartupIds, setDismissedStartupIds] = useState<Set<string>>(new Set());
  const [showDismissalToast, setShowDismissalToast] = useState(false);
  const [dismissalToastStartupId, setDismissalToastStartupId] = useState<string | null>(null);
  const [dismissalToastStartupName, setDismissalToastStartupName] = useState<string>('');
  /** Dismissal is only written after the toast expires, so Undo cannot lose a race. */
  const pendingFeedDismissalIdRef = useRef<string | null>(null);

  const functions = getFunctions();
  const profileCache = useRef<Map<string, StartupProfile>>(new Map());
  /** Avoid duplicate getStartupProfile calls while enriching a catalog stub */
  const enrichingProfileIdsRef = useRef<Set<string>>(new Set());
  const prefetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Within-row card order — stable across hover/re-renders */
  const shuffledOrdersRef = useRef<Map<string, string[]>>(new Map());
  /** Featured hero pick — stable until intentional feed regenerate */
  const headerPickRef = useRef<{
    type: 'featured' | 'recently-added' | 'specially-for-you' | 'catalog';
    startupId: string;
    title: string;
  } | null>(null);
  const prevFavoriteIdsKeyRef = useRef<string | null>(null);
  const prevPrefsKeyRef = useRef<string | null>(null);
  /** Bumped only on intentional feed regenerations (favorites / preferences / refresh) */
  const [feedLayoutEpoch, setFeedLayoutEpoch] = useState(0);
  /** Catalog scroll position to restore when closing an inline profile */
  const catalogScrollYRef = useRef(0);
  const catalogScrollFrameRef = useRef(0);
  /** Prevents ?startupId deep-link restore from reopening while we are closing the profile */
  const closingProfileRef = useRef(false);
  const hasCatalogDataRef = useRef(false);
  /** Last real catalog row a startup was opened from — used for Previously viewed badge */
  const viewCategoryByStartupIdRef = useRef<Map<string, CatalogCategory>>(new Map());
  const viewedCategoriesRef = useRef(viewedCategories);
  viewedCategoriesRef.current = viewedCategories;
  /** Latest feed rows for Previously viewed → prior-label inference */
  const categoryRowsRef = useRef<Array<{ title: string; startups: StartupProfile[] }>>([]);
  /** Deep-link ?startupId= fetch in flight (email “View Startup”) — don’t wait for the full catalog. */
  const deepLinkUserIdRef = useRef<string | null>(null);
  /** getStartupProfile already running for the open /dashboard/name URL */
  const restoringProfileIdRef = useRef<string | null>(null);

  const regenerateFeedLayout = useCallback(() => {
    shuffledOrdersRef.current.clear();
    headerPickRef.current = null;
    setFeedLayoutEpoch(epoch => epoch + 1);
  }, []);

  const clearStartupIdParam = useCallback(() => {
    if (!searchParams.has('startupId') && !searchParams.has('v')) return;
    setSearchParams(withoutStartupProfileParams(searchParams), { replace: true });
  }, [searchParams, setSearchParams]);

  const handleBack = useCallback(() => {
    closingProfileRef.current = true;
    deepLinkUserIdRef.current = null;
    forgetOpenStartupProfile();
    const y = catalogScrollYRef.current;
    setSelectedStartup(null);
    setSelectedStartupCategory(undefined);
    clearStartupIdParam();
    if (startupSlug) {
      navigate('/dashboard', { replace: true });
    }
    void refreshViewed();
    cancelAnimationFrame(catalogScrollFrameRef.current);
    catalogScrollFrameRef.current = requestAnimationFrame(() => {
      window.scrollTo({ top: y, behavior: 'auto' });
      releaseCompositorLayers();
    });
  }, [clearStartupIdParam, navigate, refreshViewed, startupSlug]);

  const selectedStartupId = selectedStartup?.id || selectedStartup?.userId;
  const followingOpenStartup = Boolean(
    selectedStartup &&
      favoriteStartups.some(
        fav => fav.id === selectedStartup.id || fav.userId === selectedStartup.id
      )
  );
  const followingOpenStartupRef = useRef(false);
  followingOpenStartupRef.current = followingOpenStartup;
  const reviewStartupIdRef = useRef<string | null>(null);
  const startupWasUnfollowedRef = useRef(false);
  const startupReviewOpenedAtRef = useRef(0);
  const startupReviewAnchorScrollRef = useRef(0);

  // First review of a startup that is not already followed. The tip waits 10s.
  // Follow, scrolling into the feeds, or leaving the profile cancels it.
  useEffect(() => {
    if (reviewStartupIdRef.current !== (selectedStartupId ?? null)) {
      reviewStartupIdRef.current = selectedStartupId ?? null;
      startupWasUnfollowedRef.current = false;
      startupReviewBlockedRef.current = false;
      startupReviewOpenedAtRef.current = Date.now();
      startupReviewAnchorScrollRef.current = 0;
      setShowStartupReviewCallout(false);
    }
    if (!selectedStartupId || user?.userType !== 'investor' || !user.id) return;

    const userId = user.id;
    if (followingOpenStartup) {
      if (!startupWasUnfollowedRef.current) return;
      startupReviewBlockedRef.current = true;
      setShowStartupReviewCallout(false);
      const recordTimer = window.setTimeout(() => recordStartupReviewVisit(userId), 0);
      return () => window.clearTimeout(recordTimer);
    }

    startupWasUnfollowedRef.current = true;
    const due = startupReviewCalloutIsDue(readStartupReviewVisit(userId));
    const showTimer = window.setTimeout(() => {
      const followVisible = Boolean(document.querySelector('[data-follow-action]'));
      const scrolledToFeeds =
        startupReviewBlockedRef.current &&
        window.scrollY >= startupReviewAnchorScrollRef.current + 80;
      const show =
        followVisible &&
        shouldShowStartupReviewCallout({
          isInvestor: true,
          reviewIsDue: due,
          alreadyFollowedStartup: followingOpenStartupRef.current,
          scrolledToFeeds,
          dismissed: false,
          elapsedMs: STARTUP_REVIEW_CALLOUT_DELAY_MS,
        });
      if (show || scrolledToFeeds || !due) recordStartupReviewVisit(userId);
      if (show) {
        const follow = document.querySelector('[data-follow-action]');
        if (follow) {
          const rect = follow.getBoundingClientRect();
          if (rect.top < 72 || rect.bottom > window.innerHeight - 16) {
            startupReviewOpenedAtRef.current = Date.now();
            follow.scrollIntoView({ block: 'center', inline: 'nearest' });
            startupReviewAnchorScrollRef.current = window.scrollY;
          }
        }
        setShowStartupReviewCallout(true);
      }
    }, STARTUP_REVIEW_CALLOUT_DELAY_MS);

    return () => window.clearTimeout(showTimer);
  }, [followingOpenStartup, selectedStartupId, user?.id, user?.userType]);

  useEffect(() => {
    if (!selectedStartupId) return;
    const onScroll = () => {
      if (Date.now() - startupReviewOpenedAtRef.current < 900) {
        startupReviewAnchorScrollRef.current = window.scrollY;
        return;
      }
      if (window.scrollY < startupReviewAnchorScrollRef.current + 80) return;
      const feeds = document.querySelector('[data-catalog-feeds]');
      if (!feeds) return;
      if (feeds.getBoundingClientRect().top < window.innerHeight * 0.72) {
        startupReviewBlockedRef.current = true;
        setShowStartupReviewCallout(false);
      }
    };
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [selectedStartupId]);

  // Opening a profile inserts it above the catalog. Chrome scroll-anchoring and
  // history scroll restoration would otherwise keep the clicked card in view.
  useLayoutEffect(() => {
    if (!selectedStartupId) return;
    jumpToPageTop();
    const frame = requestAnimationFrame(() => jumpToPageTop());
    return () => cancelAnimationFrame(frame);
  }, [selectedStartupId]);

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    return () => {
      cancelAnimationFrame(catalogScrollFrameRef.current);
    };
  }, []);

  // Close modal on Escape key
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && selectedStartup) {
        handleBack();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [selectedStartup, handleBack]);

  // Reset selected startup when "My Dashboard" is clicked (location state, not ?reset=true).
  useEffect(() => {
    const fromQuery = searchParams.get('reset') === 'true';
    const fromState = Boolean(
      (location.state as { resetDashboard?: boolean } | null)?.resetDashboard
    );
    if (!fromQuery && !fromState) return;
    forgetOpenStartupProfile();
    setSelectedStartup(null);
    setSelectedStartupCategory(undefined);
    if (fromQuery) {
      setSearchParams(withoutStartupProfileParams(searchParams), { replace: true });
    }
    if (fromState) {
      navigate(location.pathname, { replace: true, state: {} });
    }
    requestAnimationFrame(() => releaseCompositorLayers());
  }, [searchParams, setSearchParams, location.state, location.pathname, navigate]);

  useEffect(() => {
    let cancelled = false;
    const fetchStartups = async () => {
      if (!user?.id) {
        setError('User not authenticated');
        setLoading(false);
        return;
      }

      try {
        // Keep existing catalog visible on refetch — only full-page black on first load
        if (!hasCatalogDataRef.current) {
          setLoading(true);
        }
        setError(null);

        // Favorites, matching, and the catalog start together. The page stays on the
        // spinner until the catalog and matching are both in, so the rows appear
        // once in order. Lower category calls still start after that.
        const matchesTask = (async () => {
          try {
            const getInvestorMatches = httpsCallable(functions, 'getInvestorMatches');
            const result = await getInvestorMatches({ userId: user.id });
            return result.data as any;
          } catch (err) {
            console.error('Error fetching matches:', err);
            return { success: false, matches: [] };
          }
        })();
        const favoritesTask = favoritesReadyRef.current
          ? Promise.resolve({ success: true, favorites: ctxFavoritesRef.current })
          : (async () => {
              try {
                const getFavorites = httpsCallable(functions, 'getFavorites');
                const result = await getFavorites({});
                return result.data as any;
              } catch (err) {
                console.error('Error fetching favorites:', err);
                return { success: false, favorites: [] };
              }
            })();
        const allStartupsTask = (async () => {
          try {
            const getApprovedStartups = httpsCallable(functions, 'getApprovedStartups');
            const result = await getApprovedStartups();
            return result.data as any;
          } catch (err) {
            console.error('Error fetching all startups:', err);
            return { success: false, startups: [] };
          }
        })();
        const dismissalsTask = (async () => {
          try {
            const getFeedDismissals = httpsCallable(functions, 'getFeedDismissals');
            const result = await getFeedDismissals({});
            return result.data as any;
          } catch (err) {
            console.error('Error fetching feed dismissals:', err);
            return { success: false, dismissedStartupIds: [] };
          }
        })();

        const favoritesResult = await favoritesTask;
        const dismissalsResult = await dismissalsTask;
        if (cancelled) return;

        if (favoritesResult.success && favoritesResult.favorites) {
          const favoritesWithDates = favoritesResult.favorites.map((startup: any) => ({
            ...startup,
            createdAt: startup.createdAt?.seconds
              ? new Date(startup.createdAt.seconds * 1000)
              : startup.createdAt instanceof Date
                ? startup.createdAt
                : new Date(),
            updatedAt: startup.updatedAt?.seconds
              ? new Date(startup.updatedAt.seconds * 1000)
              : startup.updatedAt instanceof Date
                ? startup.updatedAt
                : new Date(),
            lastUpdatedAt: startup.lastUpdatedAt?.seconds
              ? new Date(startup.lastUpdatedAt.seconds * 1000)
              : startup.lastUpdatedAt instanceof Date
                ? startup.lastUpdatedAt
                : new Date(),
          }));
          setFavoriteStartups(favoritesWithDates);
        }

        if (dismissalsResult?.success && Array.isArray(dismissalsResult.dismissedStartupIds)) {
          setDismissedStartupIds(new Set(dismissalsResult.dismissedStartupIds));
        }

        if (cancelled) return;

        const allStartupsResult = await allStartupsTask;
        if (cancelled) return;
        if (allStartupsResult.success && allStartupsResult.startups) {
          const allWithDates = allStartupsResult.startups.map((startup: any) => ({
            ...startup,
            createdAt: startup.createdAt?.seconds
              ? new Date(startup.createdAt.seconds * 1000)
              : startup.createdAt instanceof Date
                ? startup.createdAt
                : new Date(),
            updatedAt: startup.updatedAt?.seconds
              ? new Date(startup.updatedAt.seconds * 1000)
              : startup.updatedAt instanceof Date
                ? startup.updatedAt
                : new Date(),
            lastUpdatedAt: startup.lastUpdatedAt?.seconds
              ? new Date(startup.lastUpdatedAt.seconds * 1000)
              : startup.lastUpdatedAt instanceof Date
                ? startup.lastUpdatedAt
                : new Date(),
            approvedAt: startup.approvedAt?.seconds
              ? new Date(startup.approvedAt.seconds * 1000)
              : startup.approvedAt instanceof Date
                ? startup.approvedAt
                : undefined,
            featured: startup.featured === true,
          }));
          setAllStartups(allWithDates);
        }
        hasCatalogDataRef.current = true;
        setCatalogReady(true);

        const matchesResult = await matchesTask;
        if (cancelled) return;
        if (matchesResult.success && matchesResult.matches) {
          const matchesWithDates = matchesResult.matches.map((startup: any) => ({
            ...startup,
            createdAt: startup.createdAt?.seconds
              ? new Date(startup.createdAt.seconds * 1000)
              : new Date(),
            updatedAt: startup.updatedAt?.seconds
              ? new Date(startup.updatedAt.seconds * 1000)
              : new Date(),
            lastUpdatedAt: startup.lastUpdatedAt?.seconds
              ? new Date(startup.lastUpdatedAt.seconds * 1000)
              : new Date(),
          }));
          setMatchedStartups(matchesWithDates);
        }
        if (!cancelled) {
          setMatchesReady(true);
          setLoading(false);
        }

        if (cancelled) return;

        const [
          womenLedResult,
          pocOwnedResult,
          lgbtqOwnedResult,
          recentlyAddedResult,
          sdgAlignedResult,
          mostPopularResult,
        ] = await Promise.all([
          (async () => {
            try {
              const getStartupsByCategory = httpsCallable(functions, 'getStartupsByCategory');
              const result = await getStartupsByCategory({ category: 'women-led' });
              return result.data as any;
            } catch (err) {
              console.error('Error fetching women-led startups:', err);
              return { success: false, startups: [] };
            }
          })(),
          (async () => {
            try {
              const getStartupsByCategory = httpsCallable(functions, 'getStartupsByCategory');
              const result = await getStartupsByCategory({ category: 'poc-owned' });
              return result.data as any;
            } catch (err) {
              console.error('Error fetching POC-owned startups:', err);
              return { success: false, startups: [] };
            }
          })(),
          (async () => {
            try {
              const getStartupsByCategory = httpsCallable(functions, 'getStartupsByCategory');
              const result = await getStartupsByCategory({ category: 'lgbtq-owned' });
              return result.data as any;
            } catch (err) {
              console.error('Error fetching LGBTQ+-owned startups:', err);
              return { success: false, startups: [] };
            }
          })(),
          (async () => {
            try {
              const getStartupsByCategory = httpsCallable(functions, 'getStartupsByCategory');
              const result = await getStartupsByCategory({ category: 'recently-added' });
              return result.data as any;
            } catch (err) {
              console.error('Error fetching recently added startups:', err);
              return { success: false, startups: [] };
            }
          })(),
          (async () => {
            try {
              const getStartupsByCategory = httpsCallable(functions, 'getStartupsByCategory');
              const result = await getStartupsByCategory({
                category: 'sdg-aligned',
                userId: user.id,
              });
              return result.data as any;
            } catch (err) {
              console.error('Error fetching SDG-aligned startups:', err);
              return { success: false, startups: [] };
            }
          })(),
          (async () => {
            try {
              const getMostPopularStartups = httpsCallable(functions, 'getMostPopularStartups');
              const result = await getMostPopularStartups({});
              return result.data as any;
            } catch (err) {
              console.error('Error fetching most popular startups:', err);
              return { success: false, startups: [] };
            }
          })(),
        ]);

        if (cancelled) return;

        // Process women-led startups
        if (womenLedResult.success && womenLedResult.startups) {
          const womenLedWithDates = womenLedResult.startups.map((startup: any) => ({
            ...startup,
            createdAt: startup.createdAt?.seconds
              ? new Date(startup.createdAt.seconds * 1000)
              : new Date(),
            updatedAt: startup.updatedAt?.seconds
              ? new Date(startup.updatedAt.seconds * 1000)
              : new Date(),
            lastUpdatedAt: startup.lastUpdatedAt?.seconds
              ? new Date(startup.lastUpdatedAt.seconds * 1000)
              : new Date(),
          }));
          setWomenLedStartups(womenLedWithDates);
        }

        // Process POC-owned startups
        if (pocOwnedResult.success && pocOwnedResult.startups) {
          const pocOwnedWithDates = pocOwnedResult.startups.map((startup: any) => ({
            ...startup,
            createdAt: startup.createdAt?.seconds
              ? new Date(startup.createdAt.seconds * 1000)
              : new Date(),
            updatedAt: startup.updatedAt?.seconds
              ? new Date(startup.updatedAt.seconds * 1000)
              : new Date(),
            lastUpdatedAt: startup.lastUpdatedAt?.seconds
              ? new Date(startup.lastUpdatedAt.seconds * 1000)
              : new Date(),
          }));
          setPocOwnedStartups(pocOwnedWithDates);
        }

        // Process LGBTQ+-owned startups
        if (lgbtqOwnedResult.success && lgbtqOwnedResult.startups) {
          const lgbtqOwnedWithDates = lgbtqOwnedResult.startups.map((startup: any) => ({
            ...startup,
            createdAt: startup.createdAt?.seconds
              ? new Date(startup.createdAt.seconds * 1000)
              : startup.createdAt instanceof Date
                ? startup.createdAt
                : new Date(),
            updatedAt: startup.updatedAt?.seconds
              ? new Date(startup.updatedAt.seconds * 1000)
              : startup.updatedAt instanceof Date
                ? startup.updatedAt
                : new Date(),
            lastUpdatedAt: startup.lastUpdatedAt?.seconds
              ? new Date(startup.lastUpdatedAt.seconds * 1000)
              : startup.lastUpdatedAt instanceof Date
                ? startup.lastUpdatedAt
                : new Date(),
          }));
          setLgbtqOwnedStartups(lgbtqOwnedWithDates);
        }

        // Process recently added startups
        if (recentlyAddedResult.success && recentlyAddedResult.startups) {
          const recentlyAddedWithDates = recentlyAddedResult.startups.map((startup: any) => ({
            ...startup,
            createdAt: startup.createdAt?.seconds
              ? new Date(startup.createdAt.seconds * 1000)
              : startup.createdAt instanceof Date
                ? startup.createdAt
                : new Date(),
            updatedAt: startup.updatedAt?.seconds
              ? new Date(startup.updatedAt.seconds * 1000)
              : startup.updatedAt instanceof Date
                ? startup.updatedAt
                : new Date(),
            lastUpdatedAt: startup.lastUpdatedAt?.seconds
              ? new Date(startup.lastUpdatedAt.seconds * 1000)
              : startup.lastUpdatedAt instanceof Date
                ? startup.lastUpdatedAt
                : new Date(),
            approvedAt: startup.approvedAt?.seconds
              ? new Date(startup.approvedAt.seconds * 1000)
              : startup.approvedAt instanceof Date
                ? startup.approvedAt
                : undefined,
          }));
          setRecentlyAddedStartups(recentlyAddedWithDates);
        }

        // Process SDG-aligned startups
        if (sdgAlignedResult.success && sdgAlignedResult.startups) {
          const sdgAlignedWithDates = sdgAlignedResult.startups.map((startup: any) => ({
            ...startup,
            createdAt: startup.createdAt?.seconds
              ? new Date(startup.createdAt.seconds * 1000)
              : startup.createdAt instanceof Date
                ? startup.createdAt
                : new Date(),
            updatedAt: startup.updatedAt?.seconds
              ? new Date(startup.updatedAt.seconds * 1000)
              : startup.updatedAt instanceof Date
                ? startup.updatedAt
                : new Date(),
            lastUpdatedAt: startup.lastUpdatedAt?.seconds
              ? new Date(startup.lastUpdatedAt.seconds * 1000)
              : startup.lastUpdatedAt instanceof Date
                ? startup.lastUpdatedAt
                : new Date(),
          }));
          setSdgAlignedStartups(sdgAlignedWithDates);
        }

        // Process most popular startups
        if (mostPopularResult.success && mostPopularResult.startups) {
          const mostPopularWithDates = mostPopularResult.startups.map((startup: any) => ({
            ...startup,
            createdAt: startup.createdAt?.seconds
              ? new Date(startup.createdAt.seconds * 1000)
              : startup.createdAt instanceof Date
                ? startup.createdAt
                : new Date(),
            updatedAt: startup.updatedAt?.seconds
              ? new Date(startup.updatedAt.seconds * 1000)
              : startup.updatedAt instanceof Date
                ? startup.updatedAt
                : new Date(),
            lastUpdatedAt: startup.lastUpdatedAt?.seconds
              ? new Date(startup.lastUpdatedAt.seconds * 1000)
              : startup.lastUpdatedAt instanceof Date
                ? startup.lastUpdatedAt
                : new Date(),
          }));
          setMostPopularStartups(mostPopularWithDates);
        }
      } catch (err: any) {
        console.error('Error fetching startups:', err);
        if (!cancelled) {
          setError(err.message || 'Failed to load startups. Please try again.');
        }
      } finally {
        if (!cancelled) {
          hasCatalogDataRef.current = true;
          setLoading(false);
        }
      }
    };

    fetchStartups();
    return () => {
      cancelled = true;
    };
  }, [functions, user]);

  useEffect(() => {
    if (!loading) {
      void refreshViewed();
    }
  }, [loading, refreshViewed]);

  /** Catalog list cards omit detail fields; full profile needs getStartupProfile. */
  const isCatalogStub = useCallback((startup: StartupProfile) => {
    return (
      startup.pitchSummary === undefined &&
      startup.impressiveFactor === undefined &&
      startup.url === undefined &&
      startup.traction === undefined
    );
  }, []);

  const toProfileWithDates = useCallback(
    (profile: any, originalCategory?: string): StartupProfile => {
      const profileWithDates: StartupProfile = {
        ...profile,
        createdAt: profile.createdAt?.seconds
          ? new Date(profile.createdAt.seconds * 1000)
          : profile.createdAt instanceof Date
            ? profile.createdAt
            : new Date(),
        updatedAt: profile.updatedAt?.seconds
          ? new Date(profile.updatedAt.seconds * 1000)
          : profile.updatedAt instanceof Date
            ? profile.updatedAt
            : new Date(),
        lastUpdatedAt: profile.lastUpdatedAt?.seconds
          ? new Date(profile.lastUpdatedAt.seconds * 1000)
          : profile.lastUpdatedAt instanceof Date
            ? profile.lastUpdatedAt
            : new Date(),
      };
      if (originalCategory) {
        profileWithDates.originalCategory = originalCategory;
      }
      return profileWithDates;
    },
    []
  );

  /** Load pitch summary / website / founders / etc. after opening a catalog stub. */
  const enrichSelectedStartup = useCallback(
    (startup: StartupProfile) => {
      const userId = startup.userId;
      if (!userId) return;

      const cached = profileCache.current.get(userId);
      if (cached) {
        const cachedWithCategory = startup.originalCategory
          ? { ...cached, originalCategory: startup.originalCategory }
          : cached;
        setSelectedStartup(cachedWithCategory);
        return;
      }

      if (enrichingProfileIdsRef.current.has(userId)) return;
      enrichingProfileIdsRef.current.add(userId);

      const getStartupProfile = httpsCallable(functions, 'getStartupProfile');
      getStartupProfile({ userId })
        .then(result => {
          const data = result.data as any;
          if (data.success && data.profile) {
            const profileWithDates = toProfileWithDates(data.profile, startup.originalCategory);
            profileCache.current.set(userId, profileWithDates);
            setSelectedStartup(prev => {
              const prevId = prev?.id || prev?.userId;
              const nextId = profileWithDates.id || profileWithDates.userId;
              // Don't overwrite if the investor already opened another startup
              if (prev && prevId && nextId && prevId !== nextId) return prev;
              return profileWithDates;
            });
          }
        })
        .catch(err => {
          console.error('Error fetching startup profile:', err);
        })
        .finally(() => {
          enrichingProfileIdsRef.current.delete(userId);
        });
    },
    [functions, toProfileWithDates]
  );

  // Email / inbox “View Startup” opens /dashboard?startupId= — fetch that one profile immediately.
  useEffect(() => {
    const startupIdParam = searchParams.get('startupId');
    if (!startupIdParam || closingProfileRef.current) return;

    const alreadyOpen =
      selectedStartup &&
      (selectedStartup.id === startupIdParam || selectedStartup.userId === startupIdParam);
    if (alreadyOpen) {
      deepLinkUserIdRef.current = startupIdParam;
      const slug = startupNameSlug(selectedStartup.companyName);
      if (slug && startupSlug !== slug) {
        navigate(`/dashboard/${slug}`, { replace: true });
      }
      return;
    }

    if (deepLinkUserIdRef.current === startupIdParam) return;
    deepLinkUserIdRef.current = startupIdParam;

    const cached = profileCache.current.get(startupIdParam);
    if (cached) {
      setSelectedStartup(cached);
      const slug = startupNameSlug(cached.companyName);
      if (slug) navigate(`/dashboard/${slug}`, { replace: true });
      return;
    }

    let cancelled = false;
    const getStartupProfile = httpsCallable(functions, 'getStartupProfile');
    getStartupProfile({ userId: startupIdParam })
      .then(result => {
        if (cancelled) return;
        const data = result.data as { success?: boolean; profile?: StartupProfile };
        if (!data.success || !data.profile) {
          deepLinkUserIdRef.current = null;
          return;
        }
        const profileWithDates = toProfileWithDates(data.profile);
        profileCache.current.set(startupIdParam, profileWithDates);
        setSelectedStartup(profileWithDates);
        const slug = startupNameSlug(profileWithDates.companyName);
        if (slug) navigate(`/dashboard/${slug}`, { replace: true });
      })
      .catch(err => {
        console.error('Error loading startup from deep link:', err);
        if (!cancelled) deepLinkUserIdRef.current = null;
      });

    return () => {
      cancelled = true;
    };
  }, [searchParams, selectedStartup, startupSlug, functions, toProfileWithDates, navigate]);

  const handleSelectStartup = (startup: StartupProfile, category?: CatalogCategory) => {
    const startupId = startup.id || startup.userId;

    // Remember the browse row (not Favorites / Previously viewed) for later profile badges.
    if (
      startupId &&
      category &&
      category !== CATALOG_CATEGORY_LABELS.yourFavorites &&
      category !== CATALOG_CATEGORY_LABELS.previouslyViewed
    ) {
      viewCategoryByStartupIdRef.current.set(startupId, category);
    }

    // From Your favorites / Previously viewed: keep the prior browse label (not the row name).
    // Prefer: stored click → session/server map → first visible feed row (not Women-led mega-list).
    let effectiveCategory = category;
    let resolvedPriorCategory: CatalogCategory | undefined;
    if (
      category === CATALOG_CATEGORY_LABELS.yourFavorites ||
      category === CATALOG_CATEGORY_LABELS.previouslyViewed
    ) {
      const storedViewCategory =
        (startupId && viewCategoryByStartupIdRef.current.get(startupId)) ||
        (startupId ? viewedCategories[startupId] : undefined);
      resolvedPriorCategory =
        (startup.originalCategory as CatalogCategory | undefined) ||
        storedViewCategory ||
        inferCategoryFromCatalogRows(startup, categoryRowsRef.current) ||
        (category === CATALOG_CATEGORY_LABELS.yourFavorites
          ? inferCatalogBrowsingCategory(startup, favoriteBrowseLists)
          : undefined);
      effectiveCategory = resolvedPriorCategory || CATALOG_CATEGORY_LABELS.speciallyForYou;
    }
    if (startupId) {
      pendingCatalogClick = {
        startupId,
        slug: startupNameSlug(startup.companyName),
        category: effectiveCategory,
      };
    }
    setSelectedStartupCategory(effectiveCategory);

    // If viewing from a real category row, keep originalCategory in sync for future views
    // (favorites badge + Previously viewed → prior label).
    if (
      category &&
      category !== CATALOG_CATEGORY_LABELS.yourFavorites &&
      category !== CATALOG_CATEGORY_LABELS.previouslyViewed &&
      startup.id &&
      favoriteStartups.some(fav => fav.id === startup.id)
    ) {
      setFavoriteStartups(prev =>
        prev.map(fav => (fav.id === startup.id ? { ...fav, originalCategory: category } : fav))
      );
    }

    // Remember catalog place so closing the profile can restore it
    catalogScrollYRef.current = window.scrollY;
    closingProfileRef.current = false;

    // Stamp browse context for the profile badge. Based-on-favorites row → “Since you follow …”.
    const followBadgeLabel = isSinceYouAddedFavoritesTitle(category || '')
      ? getSinceYouFollowBadgeLabel(
          selectedFavoriteForSimilar?.companyName || favoriteStartups[0]?.companyName
        )
      : undefined;
    const categoryForBadge = followBadgeLabel || category;
    const startupWithBrowseContext: StartupProfile = resolvedPriorCategory
      ? {
          ...startup,
          originalCategory:
            startup.originalCategory ||
            (isSinceYouAddedFavoritesTitle(String(resolvedPriorCategory))
              ? getSinceYouFollowBadgeLabel(
                  selectedFavoriteForSimilar?.companyName || favoriteStartups[0]?.companyName
                )
              : resolvedPriorCategory),
        }
      : categoryForBadge &&
          category !== CATALOG_CATEGORY_LABELS.yourFavorites &&
          category !== CATALOG_CATEGORY_LABELS.previouslyViewed
        ? { ...startup, originalCategory: categoryForBadge }
        : startup;
    setSelectedStartup(startupWithBrowseContext);
    // Show /dashboard/company-name in the address bar (never the raw id).
    const slug = startupNameSlug(startupWithBrowseContext.companyName);
    if (slug && startupId) {
      rememberOpenStartupProfile({ slug, userId: startupId });
    }
    if (slug && startupSlug !== slug) {
      navigate(`/dashboard/${slug}`, { replace: false });
    }

    // Track card click in the background
    if (startupId) {
      trackInteraction({
        action: 'view',
        startupId,
        source: 'catalog',
        category: category,
        metadata: {
          elementClicked: 'startup-card',
          sessionId: `session-${Date.now()}`,
        },
      }).catch(error => {
        console.error('Error tracking card click:', error);
      });
      // Only persist the row the investor actually clicked — never an inferred badge label
      if (
        category &&
        category !== CATALOG_CATEGORY_LABELS.yourFavorites &&
        category !== CATALOG_CATEGORY_LABELS.previouslyViewed
      ) {
        markViewed(startupId, category);
      } else {
        markViewed(startupId);
      }
    }

    // Enrich with full profile (pitch summary, website, founders, etc.)
    enrichSelectedStartup(startupWithBrowseContext);
  };

  const prefetchProfile = (startup: StartupProfile) => {
    const { userId } = startup;
    if (!userId || profileCache.current.has(userId)) return;
    if (prefetchTimerRef.current) clearTimeout(prefetchTimerRef.current);
    prefetchTimerRef.current = setTimeout(() => {
      if (profileCache.current.has(userId)) return;
      const getStartupProfile = httpsCallable(functions, 'getStartupProfile');
      getStartupProfile({ userId })
        .then(result => {
          const data = result.data as any;
          if (data.success && data.profile) {
            const profileWithDates: StartupProfile = {
              ...data.profile,
              createdAt: data.profile.createdAt?.seconds
                ? new Date(data.profile.createdAt.seconds * 1000)
                : new Date(),
              updatedAt: data.profile.updatedAt?.seconds
                ? new Date(data.profile.updatedAt.seconds * 1000)
                : new Date(),
              lastUpdatedAt: data.profile.lastUpdatedAt?.seconds
                ? new Date(data.profile.lastUpdatedAt.seconds * 1000)
                : new Date(),
            };
            profileCache.current.set(userId, profileWithDates);
          }
        })
        .catch(() => {
          /* prefetch failure is silent */
        });
    }, 200);
  };

  const persistFeedDismissal = useCallback(
    (startupId: string) => {
      const dismissFromFeed = httpsCallable<
        { startupId: string },
        { success: boolean; error?: string }
      >(functions, 'dismissFromFeed');
      void dismissFromFeed({ startupId }).catch(err => {
        console.error('Error dismissing from feed:', err);
      });
    },
    [functions]
  );

  const handleDismissFromFeedRequested = useCallback(
    (info: { startupId: string; companyName: string }) => {
      const startupId = info.startupId;
      const companyName = info.companyName || 'This startup';
      if (!startupId) return;

      if (pendingFeedDismissalIdRef.current && pendingFeedDismissalIdRef.current !== startupId) {
        persistFeedDismissal(pendingFeedDismissalIdRef.current);
      }
      pendingFeedDismissalIdRef.current = startupId;

      closingProfileRef.current = true;
      forgetOpenStartupProfile();
      setDismissedStartupIds(prev => new Set([...Array.from(prev), startupId]));
      setSelectedStartup(null);
      setSelectedStartupCategory(undefined);
      clearStartupIdParam();
      if (startupSlug) {
        navigate('/dashboard', { replace: true });
      }
      setDismissalToastStartupId(startupId);
      setDismissalToastStartupName(companyName);
      setShowDismissalToast(true);
    },
    [clearStartupIdParam, navigate, persistFeedDismissal, startupSlug]
  );

  const handleDismissalToastUndo = useCallback(() => {
    const startupId = dismissalToastStartupId;
    if (!startupId) return;
    const stillPending = pendingFeedDismissalIdRef.current === startupId;
    pendingFeedDismissalIdRef.current = null;
    setDismissedStartupIds(prev => {
      const next = new Set(prev);
      next.delete(startupId);
      return next;
    });
    setShowDismissalToast(false);
    setDismissalToastStartupId(null);
    setDismissalToastStartupName('');
    if (stillPending) return;
    const undoFeedDismissal = httpsCallable<{ startupId: string }, { success: boolean }>(
      functions,
      'undoFeedDismissal'
    );
    void undoFeedDismissal({ startupId }).catch(err => {
      console.error('Error undoing dismissal:', err);
    });
  }, [dismissalToastStartupId, functions]);

  const handleDismissalToastDismiss = useCallback(() => {
    const startupId = pendingFeedDismissalIdRef.current;
    pendingFeedDismissalIdRef.current = null;
    setShowDismissalToast(false);
    setDismissalToastStartupId(null);
    setDismissalToastStartupName('');
    if (startupId) persistFeedDismissal(startupId);
  }, [persistFeedDismissal]);

  // Helper function to filter startups by region
  const filterByRegion = useCallback(
    (
      startups: StartupProfile[],
      region: 'EUROPE' | 'NORTH AMERICA' | 'SOUTH AMERICA' | 'NORDICS' | 'AFRICA'
    ): StartupProfile[] => {
      const regionCountries = countryRegions[region] || [];
      return startups.filter(startup => regionCountries.includes(startup.country));
    },
    []
  );

  // Helper function to filter US-based startups
  const filterUSBased = useCallback((startups: StartupProfile[]): StartupProfile[] => {
    return startups.filter(startup => startup.country === 'United States');
  }, []);

  // Helper function to filter LATAM-based startups
  const filterLATAMBased = useCallback((startups: StartupProfile[]): StartupProfile[] => {
    const latamCountries = [
      ...countryRegions['CENTRAL AMERICA'],
      ...countryRegions['SOUTH AMERICA'],
      'Mexico', // Mexico is in NORTH AMERICA but part of LATAM culturally
    ];
    return startups.filter(startup => latamCountries.includes(startup.country));
  }, []);

  // Prefer the newest favorite for "Since you added..." (favorites list is createdAt desc)
  useEffect(() => {
    if (favoriteStartups.length > 0) {
      const newestFavorite = favoriteStartups[0];
      if (
        !selectedFavoriteForSimilar ||
        !favoriteStartups.some(fav => fav.id === selectedFavoriteForSimilar.id) ||
        selectedFavoriteForSimilar.id !== newestFavorite.id
      ) {
        setSelectedFavoriteForSimilar(newestFavorite);
      }
    } else {
      setSelectedFavoriteForSimilar(null);
    }
  }, [favoriteStartups, selectedFavoriteForSimilar]);

  // Regenerate feed layout only when the favorite *set* changes (add/remove),
  // not when metadata like originalCategory is patched on hover/open.
  useEffect(() => {
    const favoriteIdsKey = favoriteStartups
      .map(s => s.id)
      .filter(Boolean)
      .slice()
      .sort()
      .join('|');
    if (prevFavoriteIdsKeyRef.current === null) {
      prevFavoriteIdsKeyRef.current = favoriteIdsKey;
      return;
    }
    if (prevFavoriteIdsKeyRef.current !== favoriteIdsKey) {
      prevFavoriteIdsKeyRef.current = favoriteIdsKey;
      regenerateFeedLayout();
    }
  }, [favoriteStartups, regenerateFeedLayout]);

  // When matching prefs change, re-fetch Specially for you (old matches stay until refetch).
  // Also regenerate catalog row layout for location/industry rows.
  useEffect(() => {
    const prefsKey = JSON.stringify({
      industries: investorProfile?.industries ?? [],
      countries: investorProfile?.countries ?? [],
      states: investorProfile?.states ?? [],
      stages: investorProfile?.stages ?? [],
      currency: investorProfile?.currency ?? '',
      investmentRange: investorProfile?.investmentRange ?? [],
      selectedSDGs: investorProfile?.selectedSDGs ?? [],
      founderRepresentation: investorProfile?.founderRepresentation ?? [],
      country: investorProfile?.country ?? '',
    });
    if (prevPrefsKeyRef.current === null) {
      prevPrefsKeyRef.current = prefsKey;
      return;
    }
    if (prevPrefsKeyRef.current === prefsKey) return;
    prevPrefsKeyRef.current = prefsKey;

    regenerateFeedLayout();

    if (!user?.id) return;

    let cancelled = false;
    (async () => {
      try {
        const getInvestorMatches = httpsCallable(functions, 'getInvestorMatches');
        const result = await getInvestorMatches({ userId: user.id });
        const data = result.data as {
          success?: boolean;
          matches?: StartupProfile[];
        };
        if (cancelled || !data?.success) return;

        const matchesWithDates = (data.matches || []).map((startup: any) => ({
          ...startup,
          createdAt: startup.createdAt?.seconds
            ? new Date(startup.createdAt.seconds * 1000)
            : startup.createdAt instanceof Date
              ? startup.createdAt
              : new Date(),
          updatedAt: startup.updatedAt?.seconds
            ? new Date(startup.updatedAt.seconds * 1000)
            : startup.updatedAt instanceof Date
              ? startup.updatedAt
              : new Date(),
          lastUpdatedAt: startup.lastUpdatedAt?.seconds
            ? new Date(startup.lastUpdatedAt.seconds * 1000)
            : startup.lastUpdatedAt instanceof Date
              ? startup.lastUpdatedAt
              : new Date(),
        }));
        setMatchedStartups(matchesWithDates);
      } catch (err) {
        console.error('Error re-fetching matches after preference change:', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [investorProfile, regenerateFeedLayout, functions, user?.id]);

  // Based on your Favorites: all favorite industries × favorite countries
  // (or continents when country-level pool < 4). See buildBasedOnFavoritesStartups.
  const similarToFavoriteStartups = useMemo(() => {
    if (favoriteStartups.length === 0) return [];
    const excludeIds = new Set<string>();
    if (selectedStartup?.id) excludeIds.add(selectedStartup.id);
    return buildBasedOnFavoritesStartups(favoriteStartups, allStartups, { excludeIds });
  }, [favoriteStartups, allStartups, selectedStartup]);

  // Filter geographic categories
  const europeBasedStartups = useMemo(
    () => filterByRegion(allStartups, 'EUROPE'),
    [allStartups, filterByRegion]
  );
  const usBasedStartups = useMemo(() => filterUSBased(allStartups), [allStartups, filterUSBased]);
  const latamBasedStartups = useMemo(
    () => filterLATAMBased(allStartups),
    [allStartups, filterLATAMBased]
  );
  const nordicsBasedStartups = useMemo(
    () => filterByRegion(allStartups, 'NORDICS'),
    [allStartups, filterByRegion]
  );
  const africaBasedStartups = useMemo(
    () => filterByRegion(allStartups, 'AFRICA'),
    [allStartups, filterByRegion]
  );

  const favoriteBrowseLists = useMemo(
    () => ({
      matchedStartups,
      favoriteStartups,
      recentlyAddedStartups,
      womenLedStartups,
      pocOwnedStartups,
      lgbtqOwnedStartups,
      europeBasedStartups,
      usBasedStartups,
      latamBasedStartups,
      nordicsBasedStartups,
      africaBasedStartups,
      sdgAlignedStartups,
    }),
    [
      matchedStartups,
      favoriteStartups,
      recentlyAddedStartups,
      womenLedStartups,
      pocOwnedStartups,
      lgbtqOwnedStartups,
      europeBasedStartups,
      usBasedStartups,
      latamBasedStartups,
      nordicsBasedStartups,
      africaBasedStartups,
      sdgAlignedStartups,
    ]
  );

  const catalogFavoriteIds = useMemo(
    () => new Set(favoriteStartups.map(s => s.id || s.userId).filter((id): id is string => !!id)),
    [favoriteStartups]
  );

  // Open from /dashboard/company-name (and legacy ?startupId= deep links from inbox).
  // A remembered profile loads even when the catalog is still fetching, so coming
  // back later does not drop the startup that was open.
  useEffect(() => {
    if (closingProfileRef.current) return;

    const findById = (id: string) =>
      allStartups.find(s => s.id === id || s.userId === id) ||
      matchedStartups.find(s => s.id === id || s.userId === id) ||
      sdgAlignedStartups.find(s => s.id === id || s.userId === id) ||
      favoriteStartups.find(s => s.id === id || s.userId === id);

    const findBySlug = (slug: string) =>
      allStartups.find(s => startupMatchesNameSlug(s, slug)) ||
      matchedStartups.find(s => startupMatchesNameSlug(s, slug)) ||
      sdgAlignedStartups.find(s => startupMatchesNameSlug(s, slug)) ||
      favoriteStartups.find(s => startupMatchesNameSlug(s, slug));

    const startupIdParam = searchParams.get('startupId');
    if (startupIdParam) {
      if (loading) return;
      const selectedId = selectedStartup?.id || selectedStartup?.userId;
      const target = findById(startupIdParam);
      if (!selectedStartup && target) {
        const openedId = target.id || target.userId;
        const clickedCategory =
          categoryFromCatalogClick(openedId, undefined) ||
          storedBrowseCategory(viewedCategories, openedId, target.id, target.userId);
        setSelectedStartup(target);
        if (clickedCategory) {
          setSelectedStartupCategory(clickedCategory);
        } else if (!selectedStartupCategory) {
          setSelectedStartupCategory(
            inferCatalogBrowsingCategory(target, favoriteBrowseLists) ?? undefined
          );
        }
        enrichSelectedStartup(target);
        const slug = startupNameSlug(target.companyName);
        const id = target.id || target.userId;
        if (slug && id) rememberOpenStartupProfile({ slug, userId: id });
        navigate(`/dashboard/${slug}`, { replace: true });
        return;
      }
      if (selectedStartup && selectedId === startupIdParam) {
        if (isCatalogStub(selectedStartup)) enrichSelectedStartup(selectedStartup);
        const slug = startupNameSlug(selectedStartup.companyName);
        if (slug && selectedId) rememberOpenStartupProfile({ slug, userId: selectedId });
        navigate(`/dashboard/${slug}`, { replace: true });
        return;
      }
      // Still fetching the one profile — don’t drop the link.
      if (deepLinkUserIdRef.current === startupIdParam) return;
      setSearchParams(withoutStartupProfileParams(searchParams), { replace: true });
      return;
    }

    if (!startupSlug) {
      return;
    }

    // The row click already chose this label. Never paint a guessed one first.
    if (
      pendingCatalogClick?.slug === startupSlug &&
      pendingCatalogClick.category &&
      selectedStartupCategory !== pendingCatalogClick.category
    ) {
      setSelectedStartupCategory(pendingCatalogClick.category);
    }

    if (selectedStartup && startupMatchesNameSlug(selectedStartup, startupSlug)) {
      const selectedId = selectedStartup.id || selectedStartup.userId;
      if (selectedId) rememberOpenStartupProfile({ slug: startupSlug, userId: selectedId });
      // A refresh restores the profile before the row label. Put that label back.
      if (!selectedStartupCategory) {
        const stored = storedBrowseCategory(
          viewedCategories,
          selectedStartup.id,
          selectedStartup.userId
        );
        if (stored) {
          setSelectedStartupCategory(stored);
        } else if (catalogReady) {
          const inferred = inferCatalogBrowsingCategory(selectedStartup, favoriteBrowseLists);
          if (inferred) setSelectedStartupCategory(inferred);
        }
      }
      if (isCatalogStub(selectedStartup)) enrichSelectedStartup(selectedStartup);
      return;
    }

    if (!loading) {
      const target = findBySlug(startupSlug);
      if (target) {
        const id = target.id || target.userId;
        if (id) rememberOpenStartupProfile({ slug: startupSlug, userId: id });
        const clickedCategory =
          (pendingCatalogClick?.slug === startupSlug && pendingCatalogClick.category) ||
          categoryFromCatalogClick(id, undefined) ||
          storedBrowseCategory(viewedCategories, id, target.id, target.userId);
        const alreadyThisStartup =
          !!selectedStartup && (selectedStartup.id === id || selectedStartup.userId === id);
        if (!alreadyThisStartup) setSelectedStartup(target);
        if (clickedCategory) {
          if (selectedStartupCategory !== clickedCategory) {
            setSelectedStartupCategory(clickedCategory);
          }
        } else if (!alreadyThisStartup) {
          setSelectedStartupCategory(
            inferCatalogBrowsingCategory(target, favoriteBrowseLists) ?? undefined
          );
        }
        if (!alreadyThisStartup || isCatalogStub(selectedStartup)) {
          enrichSelectedStartup(alreadyThisStartup ? selectedStartup : target);
        }
        return;
      }
    }

    const remembered = readOpenStartupProfile();
    if (!remembered || remembered.slug !== startupSlug) return;
    if (restoringProfileIdRef.current === remembered.userId) return;
    const cached = profileCache.current.get(remembered.userId);
    if (cached && startupMatchesNameSlug(cached, startupSlug)) {
      setSelectedStartup(cached);
      if (!selectedStartupCategory) {
        const stored = storedBrowseCategory(
          viewedCategories,
          cached.id,
          cached.userId,
          remembered.userId
        );
        if (stored) setSelectedStartupCategory(stored);
      }
      return;
    }

    restoringProfileIdRef.current = remembered.userId;
    const getStartupProfile = httpsCallable(functions, 'getStartupProfile');
    getStartupProfile({ userId: remembered.userId })
      .then(result => {
        const data = result.data as { success?: boolean; profile?: StartupProfile };
        if (!data.success || !data.profile) {
          restoringProfileIdRef.current = null;
          return;
        }
        const profileWithDates = toProfileWithDates(data.profile);
        profileCache.current.set(remembered.userId, profileWithDates);
        setSelectedStartup(prev => {
          if (prev && (prev.id === remembered.userId || prev.userId === remembered.userId)) {
            return prev;
          }
          return profileWithDates;
        });
        const stored = storedBrowseCategory(
          viewedCategoriesRef.current,
          profileWithDates.id,
          profileWithDates.userId,
          remembered.userId
        );
        if (stored) {
          setSelectedStartupCategory(prev => prev ?? stored);
        }
      })
      .catch(err => {
        console.error('Error restoring open startup profile:', err);
        restoringProfileIdRef.current = null;
      });
  }, [
    searchParams,
    startupSlug,
    loading,
    allStartups,
    matchedStartups,
    sdgAlignedStartups,
    favoriteStartups,
    favoriteBrowseLists,
    selectedStartup,
    selectedStartupCategory,
    viewedCategories,
    catalogReady,
    enrichSelectedStartup,
    isCatalogStub,
    setSearchParams,
    navigate,
    functions,
    toProfileWithDates,
  ]);

  const handleFavoriteChange = useCallback(
    async (startupId: string, isFavorited: boolean) => {
      if (!startupId) return;

      if (isFavorited) {
        let startupToAdd: StartupProfile | undefined;

        if (selectedStartup?.id === startupId) {
          startupToAdd = selectedStartup;
        } else if (allStartups.some(s => s.id === startupId)) {
          startupToAdd = allStartups.find(s => s.id === startupId);
        } else if (matchedStartups.some(s => s.id === startupId)) {
          startupToAdd = matchedStartups.find(s => s.id === startupId);
        } else if (womenLedStartups.some(s => s.id === startupId)) {
          startupToAdd = womenLedStartups.find(s => s.id === startupId);
        } else if (pocOwnedStartups.some(s => s.id === startupId)) {
          startupToAdd = pocOwnedStartups.find(s => s.id === startupId);
        } else if (lgbtqOwnedStartups.some(s => s.id === startupId)) {
          startupToAdd = lgbtqOwnedStartups.find(s => s.id === startupId);
        } else if (recentlyAddedStartups.some(s => s.id === startupId)) {
          startupToAdd = recentlyAddedStartups.find(s => s.id === startupId);
        } else if (europeBasedStartups.some(s => s.id === startupId)) {
          startupToAdd = europeBasedStartups.find(s => s.id === startupId);
        } else if (usBasedStartups.some(s => s.id === startupId)) {
          startupToAdd = usBasedStartups.find(s => s.id === startupId);
        } else if (latamBasedStartups.some(s => s.id === startupId)) {
          startupToAdd = latamBasedStartups.find(s => s.id === startupId);
        } else if (nordicsBasedStartups.some(s => s.id === startupId)) {
          startupToAdd = nordicsBasedStartups.find(s => s.id === startupId);
        } else if (africaBasedStartups.some(s => s.id === startupId)) {
          startupToAdd = africaBasedStartups.find(s => s.id === startupId);
        } else if (sdgAlignedStartups.some(s => s.id === startupId)) {
          startupToAdd = sdgAlignedStartups.find(s => s.id === startupId);
        }

        if (startupToAdd) {
          const inferred = inferCatalogBrowsingCategory(startupToAdd, favoriteBrowseLists);
          const startupWithCategory = {
            ...startupToAdd,
            originalCategory: selectedStartupCategory ?? inferred,
          };
          setFavoriteStartups(prevFavorites => {
            const alreadyExists = prevFavorites.some(fav => fav.id === startupId);
            if (alreadyExists) {
              return prevFavorites;
            }
            // Newest first so "Since you added..." reflects the startup just favorited
            return [startupWithCategory as StartupProfile, ...prevFavorites];
          });
        }
        invalidateFavorites();
      } else {
        setFavoriteStartups(prevFavorites => prevFavorites.filter(fav => fav.id !== startupId));
        invalidateFavorites();
      }
    },
    [
      selectedStartup,
      selectedStartupCategory,
      favoriteBrowseLists,
      allStartups,
      matchedStartups,
      womenLedStartups,
      pocOwnedStartups,
      lgbtqOwnedStartups,
      recentlyAddedStartups,
      europeBasedStartups,
      usBasedStartups,
      latamBasedStartups,
      nordicsBasedStartups,
      africaBasedStartups,
      sdgAlignedStartups,
      invalidateFavorites,
    ]
  );

  const notDismissed = useCallback(
    (s: StartupProfile) => {
      const id = resolveStartupId(s);
      return !id || !dismissedStartupIds.has(id);
    },
    [dismissedStartupIds]
  );

  // Fixed feed order from profile creation (Featured stays as hero above).
  // Within-row shuffle is stable across hover; regenerateFeedLayout() on favorites / prefs / refresh.
  const categoryRows = useMemo(() => {
    const stableShuffled = (
      title: string,
      sourceStartups: StartupProfile[],
      filteredStartups: StartupProfile[]
    ): StartupProfile[] => {
      const sourceIds = sourceStartups.map(s => s.id).filter((id): id is string => !!id);
      const stored = shuffledOrdersRef.current.get(title);
      const storedSet = stored ? new Set(stored) : null;
      const needsReshuffle =
        !storedSet ||
        storedSet.size !== sourceIds.length ||
        !sourceIds.every(id => storedSet.has(id));
      let orderedIds: string[];
      if (needsReshuffle) {
        orderedIds = [...sourceIds].sort(() => Math.random() - 0.5);
        shuffledOrdersRef.current.set(title, orderedIds);
      } else {
        orderedIds = stored!;
      }
      const filteredMap = new Map(filteredStartups.map(s => [s.id, s]));
      return orderedIds.filter(id => filteredMap.has(id)).map(id => filteredMap.get(id)!);
    };

    const applyAdjacentDedupe = (
      rows: Array<{ title: string; startups: StartupProfile[] }>
    ): Array<{ title: string; startups: StartupProfile[] }> => {
      const next = [...rows];
      for (let i = 1; i < next.length; i++) {
        const prevIds = new Set(next[i - 1].startups.map(s => s.id));
        const current = next[i].startups;
        const front = current.filter(s => !prevIds.has(s.id));
        const back = current.filter(s => prevIds.has(s.id));
        if (back.length > 0) {
          next[i] = { ...next[i], startups: [...front, ...back] };
        }
      }
      return next;
    };

    const ordered = buildInvestorCatalogFeed({
      investorProfile,
      allStartups,
      matchedStartups,
      favoriteStartups,
      womenLedStartups,
      pocOwnedStartups,
      lgbtqOwnedStartups,
      mostPopularStartups,
      selectedFavorite: selectedFavoriteForSimilar,
      similarToFavoriteStartups,
      viewedStartupIds: viewedIdsOrdered,
    }).map(row => {
      const preserveOrder =
        row.title === CATALOG_CATEGORY_LABELS.speciallyForYou ||
        isSinceYouAddedFavoritesTitle(row.title) ||
        row.title === CATALOG_CATEGORY_LABELS.previouslyViewed ||
        row.title === CATALOG_CATEGORY_LABELS.yourFavorites ||
        row.title === CATALOG_CATEGORY_LABELS.mostPopular;

      let startups: StartupProfile[];
      if (preserveOrder) {
        startups =
          row.title === CATALOG_CATEGORY_LABELS.yourFavorites ||
          row.title === CATALOG_CATEGORY_LABELS.previouslyViewed
            ? row.startups
            : row.startups.filter(notDismissed);
      } else {
        startups = stableShuffled(row.title, row.startups, row.startups.filter(notDismissed));
      }

      return { title: row.title, startups };
    });

    const deduped = applyAdjacentDedupe(ordered);

    // First non-meta feed row containing each startup (feed order) — prior-label guess.
    const firstFeedCategoryById = new Map<string, string>();
    for (const row of deduped) {
      if (
        row.title === CATALOG_CATEGORY_LABELS.previouslyViewed ||
        row.title === CATALOG_CATEGORY_LABELS.yourFavorites
      ) {
        continue;
      }
      for (const startup of row.startups) {
        const id = startup.id || startup.userId;
        if (id && !firstFeedCategoryById.has(id)) {
          firstFeedCategoryById.set(id, row.title);
        }
      }
    }

    return deduped.map(row => {
      if (row.title !== CATALOG_CATEGORY_LABELS.previouslyViewed) return row;
      return {
        ...row,
        startups: row.startups.map(startup => {
          if (startup.originalCategory) return startup;
          const id = startup.id || startup.userId;
          const prior =
            (id && viewCategoryByStartupIdRef.current.get(id)) ||
            (id ? viewedCategories[id] : undefined) ||
            (id ? firstFeedCategoryById.get(id) : undefined);
          return prior ? { ...startup, originalCategory: prior } : startup;
        }),
      };
    });
  }, [
    feedLayoutEpoch,
    notDismissed,
    investorProfile,
    allStartups,
    matchedStartups,
    favoriteStartups,
    similarToFavoriteStartups,
    selectedFavoriteForSimilar,
    womenLedStartups,
    pocOwnedStartups,
    lgbtqOwnedStartups,
    mostPopularStartups,
    viewedIdsOrdered,
    viewedCategories,
  ]);

  categoryRowsRef.current = categoryRows;

  // Keep "Most relevant" head stable; optionally pin the clicked category under it.
  // Head: SFY → Your Favorites → Since you added → Previously viewed
  const visibleCategoryRows = useMemo(() => {
    const sinceYouAddedRow = categoryRows.find(row => isSinceYouAddedFavoritesTitle(row.title));
    const mostRelevantTitles = [
      CATALOG_CATEGORY_LABELS.speciallyForYou,
      CATALOG_CATEGORY_LABELS.yourFavorites,
      ...(sinceYouAddedRow ? [sinceYouAddedRow.title] : []),
      CATALOG_CATEGORY_LABELS.previouslyViewed,
    ];
    const headTitles = new Set<string>(mostRelevantTitles);
    const head = mostRelevantTitles
      .map(title => categoryRows.find(row => row.title === title))
      .filter((row): row is { title: string; startups: StartupProfile[] } => !!row)
      .filter(row => {
        if (matchesReady) return true;
        // Hold the whole top block until matching is in. Otherwise
        // Based on your Favorites paints first and the startup rows jump in above it.
        return (
          row.title !== CATALOG_CATEGORY_LABELS.speciallyForYou &&
          row.title !== CATALOG_CATEGORY_LABELS.yourFavorites &&
          !isSinceYouAddedFavoritesTitle(row.title) &&
          row.title !== CATALOG_CATEGORY_LABELS.previouslyViewed
        );
      });
    const otherRows = categoryRows.filter(row => !headTitles.has(row.title));

    if (!selectedStartup || !selectedStartupCategory) {
      return [...head, ...otherRows];
    }

    if (headTitles.has(selectedStartupCategory)) {
      return [...head, ...otherRows];
    }

    const pinnedIndex = otherRows.findIndex(
      row => row.title === selectedStartupCategory && row.startups.length > 0
    );
    if (pinnedIndex <= 0) {
      return [...head, ...otherRows];
    }

    const pinned = otherRows[pinnedIndex];
    return [
      ...head,
      pinned,
      ...otherRows.slice(0, pinnedIndex),
      ...otherRows.slice(pinnedIndex + 1),
    ];
  }, [categoryRows, selectedStartup, selectedStartupCategory, matchesReady]);

  // Hero above the feed: prefer Featured, then Recently Added, then SFY, then any startup.
  // Prefer startups in the investor's location prefs (e.g. US-only → no Morocco hero).
  const headerContent = useMemo(() => {
    if (selectedStartup || allStartups.length === 0) {
      return { type: null, startup: null, title: '' };
    }

    const notDismissedStartup = (startup: StartupProfile) => {
      const id = resolveStartupId(startup);
      return !!id && !dismissedStartupIds.has(id);
    };

    const preferredCountries = normalizePreferredCountries(
      investorProfile?.countries,
      investorProfile?.states
    );
    const inPreferredLocation = (startup: StartupProfile) =>
      startupMatchesPreferredCountries(
        startup.country,
        investorProfile?.countries,
        investorProfile?.states
      );

    const startupRecencyMs = (startup: StartupProfile) => {
      const raw = startup.approvedAt || startup.createdAt;
      if (!raw) return 0;
      const at = raw instanceof Date ? raw : new Date(raw as string | number);
      const ms = at.getTime();
      return Number.isFinite(ms) ? ms : 0;
    };

    // Newest featured first so a newly featured startup is seen right away.
    const featuredStartups = allStartups
      .filter(startup => startup.featured === true && notDismissedStartup(startup))
      .sort((a, b) => startupRecencyMs(b) - startupRecencyMs(a));

    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    const recentlyAddedFiltered = allStartups.filter(startup => {
      if (!startup.approvedAt || !notDismissedStartup(startup)) return false;
      const approvedAt =
        startup.approvedAt instanceof Date ? startup.approvedAt : new Date(startup.approvedAt);
      return approvedAt >= oneWeekAgo;
    });

    // Views do not remove a startup from SFY; favorites leave SFY (Favorites / Since you added).
    const favoriteIds = new Set(
      favoriteStartups.map(s => s.id || s.userId).filter((id): id is string => !!id)
    );
    const speciallyForYouFiltered = matchedStartups.filter(startup => {
      if (!notDismissedStartup(startup)) return false;
      const id = startup.id || startup.userId;
      return !!id && !favoriteIds.has(id);
    });
    const anyStartups = allStartups.filter(notDismissedStartup);

    type HeroCategory = {
      type: 'featured' | 'recently-added' | 'specially-for-you' | 'catalog';
      startups: StartupProfile[];
      title: string;
    };

    const preferLocation = (startups: StartupProfile[]) => {
      if (!preferredCountries.length) return startups;
      const localized = startups.filter(inPreferredLocation);
      return localized.length > 0 ? localized : [];
    };

    const pools: HeroCategory[] = [
      {
        type: 'featured',
        startups: featuredStartups,
        title: CATALOG_CATEGORY_LABELS.featuredStartup,
      },
      {
        type: 'recently-added',
        startups: recentlyAddedFiltered,
        title: CATALOG_CATEGORY_LABELS.recentlyAdded,
      },
      {
        type: 'specially-for-you',
        startups: speciallyForYouFiltered,
        title: CATALOG_CATEGORY_LABELS.speciallyForYou,
      },
      {
        type: 'catalog',
        startups: anyStartups,
        title: CATALOG_CATEGORY_LABELS.featuredStartup,
      },
    ];

    let selectedCategory: HeroCategory | null = null;
    for (const pool of pools) {
      // Featured: always use the full newest-first list (do not hide by preferred location).
      if (pool.type === 'featured' && pool.startups.length > 0) {
        selectedCategory = pool;
        break;
      }
      const localized = preferLocation(pool.startups);
      if (localized.length > 0) {
        selectedCategory = { ...pool, startups: localized };
        break;
      }
    }
    // Last resort: unfiltered featured/catalog so the hero is not blank
    if (!selectedCategory) {
      for (const pool of pools) {
        if (pool.startups.length > 0) {
          selectedCategory = pool;
          break;
        }
      }
    }

    if (!selectedCategory) {
      return { type: null, startup: null, title: '' };
    }

    // Featured hero: always the newest. Other pools stay sticky / random as before.
    if (selectedCategory.type === 'featured') {
      const newestFeatured = selectedCategory.startups[0];
      if (!newestFeatured?.id) {
        return { type: null, startup: null, title: '' };
      }
      headerPickRef.current = {
        type: 'featured',
        startupId: newestFeatured.id,
        title: selectedCategory.title,
      };
      return {
        type: 'featured',
        startup: newestFeatured,
        title: selectedCategory.title,
      };
    }

    // Keep the hero stable across hover/re-renders; only re-pick after regenerateFeedLayout().
    const storedPick = headerPickRef.current;
    if (storedPick) {
      const sameStartup = selectedCategory.startups.find(s => s.id === storedPick.startupId);
      if (sameStartup && storedPick.type === selectedCategory.type) {
        return {
          type: selectedCategory.type,
          startup: sameStartup,
          title: selectedCategory.title,
        };
      }
      const stillAvailable = selectedCategory.startups.find(s => s.id === storedPick.startupId);
      if (stillAvailable) {
        headerPickRef.current = {
          type: selectedCategory.type,
          startupId: stillAvailable.id!,
          title: selectedCategory.title,
        };
        return {
          type: selectedCategory.type,
          startup: stillAvailable,
          title: selectedCategory.title,
        };
      }
    }

    const selectedStartupFromCategory =
      selectedCategory.startups[Math.floor(Math.random() * selectedCategory.startups.length)];

    headerPickRef.current = {
      type: selectedCategory.type,
      startupId: selectedStartupFromCategory.id!,
      title: selectedCategory.title,
    };

    return {
      type: selectedCategory.type,
      startup: selectedStartupFromCategory,
      title: selectedCategory.title,
    };
  }, [
    selectedStartup,
    allStartups,
    dismissedStartupIds,
    matchedStartups,
    favoriteStartups,
    feedLayoutEpoch,
    investorProfile?.countries,
    investorProfile?.states,
  ]);
  // Loading state — never block an already-open profile on the rest of the catalog.
  const waitingOnOneProfile = Boolean(
    !selectedStartup && (searchParams.get('startupId') || startupSlug)
  );
  if (waitingOnOneProfile) {
    return (
      <div className="min-h-screen bg-[#1d1d1d] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500 mb-4"></div>
          <p className="text-white text-lg">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (loading && !selectedStartup) {
    return (
      <div className="min-h-screen bg-[#1d1d1d] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500 mb-4"></div>
          <p className="text-white text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && allStartups.length === 0) {
    return (
      <div className="min-h-screen bg-[#1d1d1d] py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto bg-gray-900 rounded-lg p-8 text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-white mb-2">Error Loading Startups</h1>
          <p className="text-gray-400 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-emerald-700 hover:bg-emerald-800 text-white font-semibold px-6 py-2 rounded-lg transition-colors duration-200"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Main view - catalog always visible; profile opens as modal overlay
  return (
    <div
      className={`min-h-screen flex flex-col pt-0 bg-[#1d1d1d] ${
        selectedStartup ? 'overflow-visible' : 'overflow-x-hidden'
      }`}
      style={{ overflowAnchor: 'none' }}
    >
      {/* ── Profile (inline at top of page) ── */}
      {selectedStartup && (
        <div className="relative z-20 w-full overflow-visible pt-4 min-h-[100dvh] md:pt-5 lg:min-h-0 lg:pt-6">
          <StartupProfileComponent
            profile={selectedStartup}
            compactDeck={true}
            flushColumnGutter
            onClose={handleBack}
            onDismissFromFeedRequested={handleDismissFromFeedRequested}
            trackingSource="catalog"
            trackingCategory={
              selectedStartupCategory && isSinceYouAddedFavoritesTitle(selectedStartupCategory)
                ? getSinceYouFollowBadgeLabel(
                    selectedFavoriteForSimilar?.companyName || favoriteStartups[0]?.companyName
                  )
                : selectedStartupCategory
            }
            isFavorited={favoriteStartups.some(fav => fav.id === selectedStartup.id)}
            onFavoriteChange={handleFavoriteChange}
            onMarkViewed={markViewed}
          />
        </div>
      )}

      {/* Featured / hero — desktop only; original overlap with Specially for you tab */}
      {headerContent.startup && !selectedStartup && (
        <div className="hidden sm:block w-full relative z-0 -mt-12 md:-mt-[72px]">
          <FeaturedStartup
            startup={headerContent.startup}
            onViewStartup={() => handleSelectStartup(headerContent.startup!, headerContent.title)}
            sectionTitle={headerContent.title}
          />
        </div>
      )}

      {selectedStartup ? (
        <div className="relative z-20 w-full" aria-hidden="true">
          <div className="h-0.5 w-full" style={{ backgroundColor: PRAIRI_SURFACES.pitchDeck }} />
        </div>
      ) : null}
      <div
        data-catalog-feeds
        className={`relative z-10 w-full ${selectedStartup ? 'mt-0' : 'mt-10 sm:-mt-24 lg:-mt-10'}`}
      >
        {allStartups.length === 0 && !loading && catalogReady ? (
          <div className="bg-gray-900 rounded-lg p-12 text-center mx-4 lg:mx-6">
            <div className="text-6xl mb-4">🔍</div>
            <h2 className="text-2xl font-bold text-white mb-2">No Startups Yet</h2>
            <p className="text-gray-400">
              There are no approved startups at the moment. Check back soon!
            </p>
          </div>
        ) : (
          <>
            {visibleCategoryRows.map((category, index) => {
              const isFirst = index === 0;
              return (
                <div
                  key={category.title}
                  className={`pl-3 sm:pl-4 lg:pl-6 mb-8 relative z-10 ${
                    isFirst && selectedStartup ? 'mt-3 pt-0 lg:mt-12' : 'mt-6'
                  }`}
                >
                  <StartupRow
                    title={category.title}
                    startups={category.startups}
                    onSelectStartup={handleSelectStartup}
                    onPrefetchStartup={prefetchProfile}
                    viewedIds={viewedIds}
                    favoriteIds={catalogFavoriteIds}
                    hasOpenProfile={!!selectedStartup}
                    hidePreviouslyViewedBadge={
                      category.title === CATALOG_CATEGORY_LABELS.previouslyViewed ||
                      category.title === CATALOG_CATEGORY_LABELS.yourFavorites
                    }
                    favoriteBadgeIconOnly={category.title === CATALOG_CATEGORY_LABELS.yourFavorites}
                  />
                </div>
              );
            })}
          </>
        )}
      </div>

      {showDashboardWelcome && catalogHome && !selectedStartup && (
        <DashboardWelcomeCallout onDismiss={() => setShowDashboardWelcome(false)} />
      )}

      {showStartupReviewCallout && selectedStartup && (
        <StartupReviewCallout
          onDismiss={() => {
            startupReviewBlockedRef.current = true;
            setShowStartupReviewCallout(false);
          }}
        />
      )}

      {showDismissalToast && (
        <DismissalConfirmationToast
          key={dismissalToastStartupId ?? 'dismissal-toast'}
          startupName={dismissalToastStartupName}
          onUndo={handleDismissalToastUndo}
          onAutoDismiss={handleDismissalToastDismiss}
        />
      )}

      {profileUpdatedToastMessage && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100000] px-5 py-3 bg-[#1A1A1A] border border-gray-700 rounded-xl shadow-2xl max-w-[90vw] flex items-center gap-3"
          role="status"
          aria-live="polite"
        >
          <svg
            className="w-5 h-5 text-emerald-400 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <p className="text-white text-sm font-medium">{profileUpdatedToastMessage}</p>
        </div>
      )}
    </div>
  );
};

export default InvestorCatalogDashboard;
